/**
 * Ink — экземпляр переходов.
 *
 * Класс, а не набор функций, потому что состояние здесь настоящее: сид,
 * пул готовых форм, история выбора механизма, конфиг проекта. В глобальных
 * переменных всё это означало бы, что два независимых блока на одной
 * странице не могут иметь разные настройки, — а именно это и требуется
 * от переносимого модуля.
 */

import { bake, type Sheet } from './core/bake'
import { thresholds, TEMPO, type Tempo } from './core/pacing'
import { hashKey, randomSeed, rng } from './core/rng'
import { BY_ID } from './fields'
import type { Mechanism } from './fields/types'

export type Quality = 'auto' | 'high' | 'low' | 'off'

/** Как выбирается механизм. Подробности — в docs/plan/12-ink-module.md, §5. */
export type Pick =
  | 'shuffle'
  | 'random'
  | 'sticky'
  | { weights: Record<string, number> }
  | string

/**
 * Уровень, на котором происходит переход.
 *
 * Не размер, а СТРУКТУРНАЯ РОЛЬ — и это оказалось правильной осью.
 * Попытка выровнять механизмы по громкости упиралась в то, что они
 * разной природы; вместо этого громкость назначается уровнем:
 *
 *   screen — смена экрана, раздела, маршрута. Это событие, и оно
 *            звучит материалом: порог, капилляр, кракелюр.
 *   object — блок, карта, попап, модалка внутри экрана. Не событие,
 *            поэтому звучит глубиной: дыхание.
 *
 * Так движение начинает кодировать иерархию, а не спорить само с собой.
 */
export type Scope = 'screen' | 'object'

export interface InkConfig {
  seed?: number
  /** Множитель ко всем длительностям. */
  speed?: number
  pick?: Pick
  /** Механизмы для смены экранов. */
  screen?: string[]
  /** Механизмы для объектов внутри экрана. */
  object?: string[]
  quality?: Quality
  /** Сколько форм на механизм держать в пуле. */
  poolSize?: number
  colors?: { bleed?: string; glow?: string }
  /**
   * Рисовать ли подтёк — цветное пятно впереди содержимого.
   *
   * По умолчанию нет. Приём хорош на бумаге и на прогорании, но требует
   * от потребителя радиуса скругления и запаса за края; без них он ложится
   * прямоугольной плашкой поверх скруглённого блока. Включается осознанно
   * и вместе с `--ink-radius`.
   */
  bleed?: boolean
  tempo?: Tempo
}

export interface PlayRequest {
  /** Размеры элемента в CSS-пикселях. */
  width: number
  height: number
  /** Разводит соседние элементы одного списка. */
  salt?: number
  /** Разово перебить механизм. */
  mechanism?: string
  /** Разово перебить сид. */
  seed?: number
  /** Уровень перехода. По умолчанию `object`: их на порядок больше. */
  scope?: Scope
}

/** Всё, что нужно, чтобы проиграть переход. */
export interface Prepared {
  mechanism: Mechanism | null
  sheet: Sheet | null
  /** Вертикальный сдвиг и растяжение маски: бесплатная вариация подачи. */
  offsetY: string
  stretchY: string
  drift: { x: string; y: string }
}

/**
 * Наборы для случайного выбора.
 *
 * Намеренно уже, чем список механизмов. В мешок попадает только то,
 * что при ЛЮБОМ сиде читается как приём, а не как дефект. Остальные
 * никуда не делись — их можно назначить явно, — но случайность обязана
 * быть безопасной: механизм, который иногда красив, а иногда похож
 * на артефакт, портит не себя, а доверие ко всем переходам сразу.
 *
 * Печатное семейство и рваный край пока не здесь: им нужна доводка
 * по громкости и по разбросу параметров.
 */
const DEFAULT_SCREEN = ['threshold', 'capillary', 'crackle']
const DEFAULT_OBJECT = ['breath']

const FRAMES = 14

/**
 * Во сколько раз лист печётся мельче элемента и сколько пикселей ему
 * позволено занять.
 *
 * Печь один в один нельзя: стоимость — это N кадров × площадь, и на полном
 * экране она вылезает из бюджета вчетверо. Значит лист печётся мельче
 * и увеличивается браузером, а платой за это надо управлять с двух сторон:
 * поле обязано быть сглаженным (иначе увеличение раздувает ступеньку
 * в блок), а всё периодическое обязано считать свой шаг в экранных
 * пикселях, а не в пикселях поля.
 *
 * Прежние 132 пикселя по ширине давали на блоке увеличение в 2.6 раза,
 * и ячейка дизеринга в 8 пикселей превращалась на экране в двадцать.
 * Это и было «дробление» на буквах.
 */
const DETAIL: Record<Exclude<Quality, 'auto' | 'off'>, { fraction: number; maxArea: number }> = {
  high: { fraction: 0.8, maxArea: 34000 },
  low: { fraction: 0.5, maxArea: 14000 },
}

export class Ink {
  #seed: number
  #cfg: Required<Omit<InkConfig, 'seed' | 'colors' | 'tempo'>> & {
    colors: { bleed?: string; glow?: string }
    tempo?: Tempo
  }
  #cache = new Map<string, Sheet>()
  /** Мешок: механизм не повторяется, пока не выпали все. */
  #bag: string[] = []
  #stickyChoice: string | null = null

  constructor(config: InkConfig = {}) {
    this.#seed = config.seed ?? randomSeed()
    this.#cfg = {
      speed: config.speed ?? 1,
      pick: config.pick ?? 'shuffle',
      screen: config.screen ?? DEFAULT_SCREEN,
      object: config.object ?? DEFAULT_OBJECT,
      quality: config.quality ?? 'auto',
      poolSize: config.poolSize ?? 5,
      colors: config.colors ?? {},
      bleed: config.bleed ?? false,
      tempo: config.tempo,
    }
  }

  get seed(): number {
    return this.#seed
  }

  /**
   * Смена сида сбрасывает всё производное.
   *
   * Иначе кэш пережил бы смену сида и продолжил отдавать старые формы —
   * то есть сид перестал бы что-либо значить, и это заметили бы не сразу.
   */
  set seed(value: number) {
    this.#seed = value >>> 0
    this.#dropCache()
    this.#bag = []
    this.#stickyChoice = null
  }

  reseed(seed?: number): number {
    this.seed = seed ?? randomSeed()
    return this.#seed
  }

  configure(patch: InkConfig): void {
    if (patch.seed !== undefined) this.seed = patch.seed
    Object.assign(this.#cfg, {
      ...patch,
      colors: { ...this.#cfg.colors, ...(patch.colors ?? {}) },
    })
    // Смена набора механизмов делает мешок недействительным: в нём могли
    // остаться те, кого только что выключили.
    this.#bag = []
  }

  get config(): Readonly<InkConfig> {
    return { ...this.#cfg, seed: this.#seed }
  }

  get colors(): { bleed?: string; glow?: string } {
    return this.#cfg.colors
  }

  get bleed(): boolean {
    return this.#cfg.bleed
  }

  /**
   * Набор, доступный на этом уровне и на такой площади.
   *
   * Уровень решает характер, площадь — страховка: механизму, которому
   * нужна ячейка растра или амплитуда кромки, на бейдже делать нечего —
   * он там читается как дрожь.
   */
  available(width: number, height: number, scope: Scope = 'object'): Mechanism[] {
    const minSide = Math.min(width, height)
    const ids = scope === 'screen' ? this.#cfg.screen : this.#cfg.object
    return ids
      .map((id) => BY_ID[id])
      .filter((m): m is Mechanism => Boolean(m) && minSide >= m!.minSide)
  }

  /**
   * Выбрать механизм.
   *
   * Мешок вместо честного рандома: при равновероятном выборе на экране
   * с десятком блоков один и тот же механизм выпадает подряд два-три раза
   * примерно в половине случаев, и такой экран выглядит не случайным,
   * а сломанным.
   */
  #choose(pool: Mechanism[], salt: number): Mechanism | null {
    if (pool.length === 0) return null

    const pick = this.#cfg.pick
    if (typeof pick === 'string' && BY_ID[pick]) {
      return pool.find((m) => m.id === pick) ?? BY_ID[pick]!
    }

    const rnd = rng(hashKey(this.#seed, 'pick', salt, this.#bag.length))

    if (pick === 'sticky') {
      if (!this.#stickyChoice || !pool.some((m) => m.id === this.#stickyChoice)) {
        this.#stickyChoice = pool[Math.floor(rnd() * pool.length)]!.id
      }
      return pool.find((m) => m.id === this.#stickyChoice) ?? pool[0]!
    }

    if (typeof pick === 'object' && 'weights' in pick) {
      const total = pool.reduce((sum, m) => sum + (pick.weights[m.id] ?? 1), 0)
      let r = rnd() * total
      for (const m of pool) {
        r -= pick.weights[m.id] ?? 1
        if (r <= 0) return m
      }
      return pool[pool.length - 1]!
    }

    if (pick === 'random') return pool[Math.floor(rnd() * pool.length)]!

    // shuffle
    this.#bag = this.#bag.filter((id) => pool.some((m) => m.id === id))
    if (this.#bag.length === 0) this.#bag = pool.map((m) => m.id)
    const idx = Math.floor(rnd() * this.#bag.length)
    const [id] = this.#bag.splice(idx, 1)
    return pool.find((m) => m.id === id) ?? pool[0]!
  }

  /**
   * Какой механизм достанется элементу такого размера.
   *
   * Публичный шов поверх политики выбора: им пользуется `prepare`, панель
   * настроек (показать, что выпадет) и тесты. Вызов ПРОДВИГАЕТ мешок —
   * это и есть выбор, а не предсказание.
   */
  choose(width: number, height: number, salt = 0, scope: Scope = 'object'): Mechanism | null {
    return this.#choose(this.available(width, height, scope), salt)
  }

  #detail(): { fraction: number; maxArea: number } | null {
    const q = this.#cfg.quality
    if (q === 'off') return null
    if (q === 'high' || q === 'low') return DETAIL[q]

    if (typeof document !== 'undefined') {
      const motion = document.documentElement.getAttribute('data-motion')
      if (motion === 'off') return null
      if (motion === 'calm') return DETAIL.low
    }
    if (typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return null
    }
    return DETAIL.high
  }

  /**
   * Посчитать всё, что нужно для перехода, ничего не показывая.
   *
   * Существует ради модалок: лист считается в момент, когда человек навёл
   * палец на кнопку, а не в первый кадр открытия. Разница между «открылось»
   * и «дёрнулось и открылось» — ровно эти несколько миллисекунд.
   */
  prepare(req: PlayRequest): Prepared {
    const empty: Prepared = {
      mechanism: null,
      sheet: null,
      offsetY: '50%',
      stretchY: '100%',
      drift: { x: '0px', y: '0px' },
    }

    const detail = this.#detail()
    if (detail === null) return empty

    const salt = req.salt ?? 0
    const pool = this.available(req.width, req.height, req.scope ?? 'object')
    const chosen = req.mechanism ? (BY_ID[req.mechanism] ?? null) : this.#choose(pool, salt)
    void pool
    if (!chosen) return empty

    // Форма берётся из ленивого пула, а подача — бесплатно и каждый раз своя.
    // Поэтому один сид на сессию не означает одинаковых переходов.
    const showRnd = rng(hashKey(req.seed ?? this.#seed, 'show', chosen.id, salt))
    const shape = Math.floor(showRnd() * this.#cfg.poolSize)

    // В ключе — округлённый размер, а не точный: поворот телефона на пиксель
    // не должен обнулять кэш и менять форму.
    const bucketW = Math.round(req.width / 40) * 40
    const bucketH = Math.round(req.height / 40) * 40
    const key = `${req.seed ?? this.#seed}|${chosen.id}|${shape}|${bucketW}x${bucketH}|${detail.fraction}`

    // Дыхание листа не имеет: размывается содержимое, а не форма.
    // Печь для него нечего, поэтому ветка уходит раньше кэша.
    if (chosen.fieldless) {
      return { ...empty, mechanism: chosen }
    }

    let sheet = this.#cache.get(key)
    if (!sheet) {
      sheet = this.#bakeSheet(chosen, req, detail, shape)
      this.#cache.set(key, sheet)
      this.#evict()
    }

    return {
      mechanism: chosen,
      sheet,
      // Лист печётся ровно по пропорции элемента и не растягивается.
      // Растяжение по вертикали давало бесплатную вариацию, но искажало
      // всё периодическое: растр под 45° превращался в растр под 60°,
      // а круглая точка — в овал. Разнообразие берётся из пула форм.
      offsetY: '0%',
      stretchY: '100%',
      drift: {
        x: `${((showRnd() * 2 - 1) * 9).toFixed(1)}px`,
        y: `${((showRnd() * 2 - 1) * 9).toFixed(1)}px`,
      },
    }
  }

  #bakeSheet(
    mech: Mechanism,
    req: PlayRequest,
    detail: { fraction: number; maxArea: number },
    shape: number,
  ): Sheet {
    const ratio = req.height / Math.max(1, req.width)

    let w = Math.max(16, Math.round(req.width * detail.fraction))
    let h = Math.max(16, Math.round(w * ratio))
    // Площадь ограничена, а не сторона: у высокого экрана и широкой карточки
    // при одинаковой ширине совсем разная цена.
    const area = w * h
    if (area > detail.maxArea) {
      const k = Math.sqrt(detail.maxArea / area)
      w = Math.max(16, Math.round(w * k))
      h = Math.max(16, Math.round(h * k))
    }

    const scale = w / Math.max(1, req.width)
    const rnd = rng(hashKey(this.#seed, 'shape', mech.id, shape))
    const field = mech.field({ w, h, rnd, scale })
    const levels = thresholds(field, FRAMES, this.#cfg.tempo ?? mech.tempo ?? TEMPO.linear!)

    // Чем сильнее лист увеличивается, тем шире должна быть переходная
    // полоса: увеличение растягивает и её, и резкая граница при увеличении
    // в два раза читается как пиксельная лесенка.
    const magnify = Math.min(3, 1 / Math.max(0.2, scale))

    return bake(field, w, h, levels, {
      frames: FRAMES,
      feather: mech.feather * magnify,
      lag: mech.lag * Math.min(1.6, magnify),
    })
  }

  /**
   * Длительность перехода в миллисекундах.
   *
   * Числа занижены против первой версии примерно на 40%: на живом экране
   * прежний темп читался не как ритуал, а как задержка. Медленность
   * остаётся доступной множителем, но по умолчанию приложение быстрое.
   */
  duration(width: number, height: number): number {
    const side = Math.max(width, height)
    const base = side <= 120 ? 250 : side <= 300 ? 370 : 540
    return base * this.#cfg.speed
  }

  #evict(): void {
    // Кэш держит формы всего нескольких размеров: экранов мало, а модалка
    // переоткрывается в тех же габаритах. Потолок — страховка от роста
    // на длинной сессии, а не рабочий режим.
    const LIMIT = 24
    while (this.#cache.size > LIMIT) {
      const oldest = this.#cache.keys().next().value as string | undefined
      if (oldest === undefined) break
      this.#cache.get(oldest)?.release()
      this.#cache.delete(oldest)
    }
  }

  #dropCache(): void {
    for (const sheet of this.#cache.values()) sheet.release()
    this.#cache.clear()
  }

  /** Отпустить все листы. Вызывается при размонтировании приложения. */
  dispose(): void {
    this.#dropCache()
  }
}

export { MECHANISMS, BY_ID } from './fields'
export type { Mechanism } from './fields/types'
